import { callOpenAI } from './client';
import { generateBatchTranslationPrompt, SYSTEM_PROMPTS } from './prompts';
import {
  extractJSON,
  validateTranslationKeys,
  isWithinTokenLimit,
  chunkTranslations,
} from './utils';

export interface TranslationResult {
  translations: Record<string, string>;
  tokenUsage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

/**
 * Translates a complete translations object from one language to another
 */
export const translateBatch = async (
  translations: Record<string, string>,
  sourceLang: string,
  targetLang: string
): Promise<TranslationResult> => {
  // Check if we need to chunk the translations
  if (!isWithinTokenLimit(translations)) {
    console.warn('⚠️ Translations exceed token limit, chunking...');
    return await translateInChunks(translations, sourceLang, targetLang);
  }

  // Single translation call
  const prompt = generateBatchTranslationPrompt(
    sourceLang,
    targetLang,
    translations
  );

  try {
    const { content, usage } = await callOpenAI([
      {
        role: 'system',
        content: SYSTEM_PROMPTS.JSON_TRANSLATOR,
      },
      {
        role: 'user',
        content: prompt,
      },
    ]);

    if (!content) {
      throw new Error('No response from OpenAI');
    }

    const translatedData = extractJSON(content);

    // Validate that all keys are preserved
    const validation = validateTranslationKeys(translations, translatedData);
    if (!validation.valid) {
      console.warn('⚠️ Translation validation issues:', validation);

      // Add missing keys with original values
      validation.missingKeys.forEach(key => {
        translatedData[key] = translations[key];
      });
    }

    return {
      translations: translatedData,
      tokenUsage: usage,
    };
  } catch (error) {
    console.error(`❌ Error translating to ${targetLang}:`, error);
    throw error;
  }
};

/**
 * Translates large translation objects by splitting into chunks
 */
const translateInChunks = async (
  translations: Record<string, string>,
  sourceLang: string,
  targetLang: string
): Promise<TranslationResult> => {
  const chunks = chunkTranslations(translations);

  const results: Record<string, string> = {};
  let totalTokens = 0;
  let promptTokens = 0;
  let completionTokens = 0;

  for (let i = 0; i < chunks.length; i++) {
    const result = await translateBatch(chunks[i], sourceLang, targetLang);

    Object.assign(results, result.translations);

    if (result.tokenUsage) {
      promptTokens += result.tokenUsage.promptTokens;
      completionTokens += result.tokenUsage.completionTokens;
      totalTokens += result.tokenUsage.totalTokens;
    }

    // Small delay between chunks to avoid rate limiting
    if (i < chunks.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }

  return {
    translations: results,
    tokenUsage: {
      promptTokens,
      completionTokens,
      totalTokens,
    },
  };
};

/**
 * Translates to multiple target languages
 */
export const translateToMultipleLanguages = async (
  translations: Record<string, string>,
  sourceLang: string,
  targetLangs: string[]
): Promise<Record<string, Record<string, string>>> => {
  const pairs = await Promise.all(
    targetLangs.map(async targetLang => {
      const result = await translateBatch(translations, sourceLang, targetLang);
      return [targetLang, result.translations] as const;
    })
  );

  return Object.fromEntries(pairs);
};
